/**
 * DeptTabBoard — Iter-156 · Phase 4 tab-and-board UI standard.
 *
 * Every department integration going forward mounts inside this
 * primitive: a lean tab row + a single active board area below.
 * At most ONE board renders at a time; the dashboard itself stays
 * uncluttered. Existing widgets can be migrated by wrapping them
 * as a tab entry ({ key, label, icon, testid, render: () => <JSX/> }).
 */
import { useState } from 'react';
import { Card } from '../ui/card';
import { useAdminScopeVersion } from '../../lib/adminScopeTransport';
import { useAdminScopeTerms } from '../../lib/adminScopeTerms';
import { useDepartmentMenu } from './DepartmentNavigation';

export default function DeptTabBoard({
  tabs = [],
  initialKey = null,
  accent = '#C9A962',
  testidPrefix = 'dept-tab-board',
}) {
  const first = initialKey || (tabs[0] && tabs[0].key) || null;
  const [active, setActive] = useState(first);
  const scopeVersion = useAdminScopeVersion();
  const terms = useAdminScopeTerms();
  const current = tabs.find((t) => t.key === active);
  const choose = useDepartmentMenu(testidPrefix, 'Command boards', tabs.map(t => ({ ...t, label: terms.text(t.label) })), active, setActive, testidPrefix);
  if (tabs.length === 0) return null;
  return (
    <div className="space-y-4" data-testid={testidPrefix}>
      {/* Tab row */}
      <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-tab-row`}>
        {tabs.map((t) => {
          const Icon = t.icon;
          const isActive = t.key === active;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => choose(t.key)}
              className="inline-flex max-w-full whitespace-normal text-left items-center gap-2 rounded-full px-4 py-2 text-xs font-bold uppercase border-2 transition"
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                letterSpacing: '0.06em',
                backgroundColor: isActive ? accent : 'transparent',
                color: isActive ? '#111' : accent,
                borderColor: accent,
              }}
              data-testid={t.testid || `${testidPrefix}-tab-${t.key}`}
              aria-pressed={isActive}
            >
              {Icon && <Icon className="h-3.5 w-3.5" />}
              {terms.text(t.label)}
              {t.badge != null && (
                <span
                  className="inline-flex items-center justify-center rounded-full text-[10px] font-bold px-1.5"
                  style={{ backgroundColor: isActive ? '#111' : accent, color: isActive ? accent : '#111', minWidth: '18px', height: '18px' }}
                >
                  {t.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {/* Active board */}
      <Card key={scopeVersion} className="p-5 bg-zinc-900 border-zinc-700" data-testid={`${testidPrefix}-active-board`}>
        {current ? current.render() : (
          <p className="text-sm text-zinc-500">Select a tab above.</p>
        )}
      </Card>
    </div>
  );
}
